import { Button, Container, Grid, Paper, TextField, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import { formatDateTime } from 'utils/date.utils';

interface Props {
  textValue: string;
  onTextChange(e: React.ChangeEvent<HTMLInputElement>): void;
  nextStep(): void;
  keyDown(): void;
  keyUp(): void;
}

const MIN_WORDS = 100;
const WRITING_TIME_SECONDS = 60 * 5;
const COUNTDOWN_SECONDS = 60;

const SurveyText: React.FC<Props> = ({ textValue, onTextChange, nextStep, keyDown, keyUp }) => {
  const { t } = useTranslation();
  const { classes } = useStyles();
  const [writingTimer, setWritingTimer] = useState(WRITING_TIME_SECONDS);
  const [countdownTimer, setCountdownTimer] = useState(COUNTDOWN_SECONDS);

  useEffect(() => {
    const timer =
      writingTimer > 0
        ? setInterval(() => setWritingTimer((prev) => prev - 1), 1000)
        : countdownTimer > 0
          ? setInterval(() => setCountdownTimer((prev) => prev - 1), 1000)
          : undefined;

    if (countdownTimer === 0) {
      nextStep();
    }
    return () => {
      if (timer) clearInterval(timer);
    };
  }, [writingTimer, countdownTimer]);

  const getWordCount = (text: string): number => {
    return text
      .split(' ')
      .filter((word) => word !== '').length;
  };

  const wordCount = getWordCount(textValue);
  const isWritingPhase = writingTimer > 0;

  const showTime = (time: number) => {
    const timeToDate = new Date(0, 0, 0, 0, 0, time, 0);
    return formatDateTime(timeToDate + '', 'mm:ss');
  };

  return (
    <Container maxWidth="xl">
      <Paper className={classes.paper} elevation={1}>
        <Grid container spacing={2}>
          <Grid size={12}>
            <TextField
              fullWidth
              label={t('enterTextHere')}
              variant="outlined"
              multiline
              margin="normal"
              minRows={14}
              maxRows={22}
              onChange={onTextChange}
              onKeyDown={keyDown}
              onKeyUp={keyUp}
              value={textValue}
            />
          </Grid>
          <Grid size={12}>
            <Grid container justifyContent="space-between" alignItems="center">
              <Grid>
                <Typography
                  variant="body2"
                  className={wordCount >= MIN_WORDS ? classes.wordCountValid : classes.wordCountPending}
                >
                  {t('soWordCount', { words: wordCount, minWords: MIN_WORDS })} *
                </Typography>
              </Grid>
              <Grid>
                <Typography
                  variant="body2"
                  className={isWritingPhase ? classes.timerNormal : classes.timerUrgent}
                >
                  {isWritingPhase ? t('timeToWrite') : t('timeToNextPicture')}:{' '}
                  {showTime(isWritingPhase ? writingTimer : countdownTimer)}
                </Typography>
              </Grid>
            </Grid>
          </Grid>
          <Grid size={12} className={classes.bottomRow}>
            <Grid container justifyContent="space-between" alignItems="flex-end">
              <Grid size={{ xs: 12, sm: 8 }}>
                <Typography variant="body2" color="textSecondary">
                  * {t('soWordHint')}
                </Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }} className={classes.buttonCol}>
                <Button onClick={nextStep} color="primary" variant="contained" size="large" disabled={wordCount <= 0}>
                  {t('continue')}
                </Button>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Paper>
    </Container>
  );
};

const useStyles = makeStyles()((theme) => ({
  paper: {
    padding: theme.spacing(3),
    borderRadius: theme.spacing(1.5),
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(2),
    },
  },
  wordCountPending: {
    color: theme.palette.text.secondary,
    marginLeft: theme.spacing(1),
  },
  wordCountValid: {
    color: theme.palette.success.main,
    marginLeft: theme.spacing(1),
  },
  timerNormal: {
    color: theme.palette.text.secondary,
    marginRight: theme.spacing(1),
  },
  timerUrgent: {
    color: theme.palette.error.main,
    marginRight: theme.spacing(1),
  },
  bottomRow: {
    marginTop: theme.spacing(2),
  },
  buttonCol: {
    textAlign: 'right',
    [theme.breakpoints.down('sm')]: {
      textAlign: 'center',
      marginTop: theme.spacing(2),
    },
  },
}));

export default React.memo(SurveyText);
